import { requestEndpoint } from "../data/requestEndpoint";
import { post } from "./useFetchAction";

export class AuthV2Service {
  static async startAuth(identifier, captchaToken) {
    try {
      const response = await post(
        requestEndpoint.authV2.start,
        { identifier, captchaToken },
        {},
      );
      return response;
    } catch (error) {
      // Cas spéciaux sur /auth/start : renvoyer les infos sans throw
      // pour que le panel gère la navigation
      if (error.response?.status === 409) {
        const key = error.response?.data?.errorKey;
        if (key === "USER_ALREADY_EXISTS" || key === "IS_SECONDARY_CONTACT") {
          return {
            data: {
              errorKey: key,
              identifierType: error.response.data.identifierType,
              requiresPassword: error.response.data.requiresPassword,
              message: error.response.data.message,
            },
          };
        }
      }
      throw this.handleError(error);
    }
  }

  static async verifyOtp(identifier, code) {
    try {
      const response = await post(
        requestEndpoint.authV2.verifyOtp,
        { identifier, code },
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async resendOtp(identifier) {
    try {
      const response = await post(
        requestEndpoint.authV2.resendOtp,
        { identifier },
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async completeRegistration(data) {
    try {
      const response = await post(requestEndpoint.authV2.complete, data, {});
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async recoverSecondaryContact(primaryIdentifier, captchaToken) {
    try {
      const response = await post(
        requestEndpoint.authV2.recoverSecondary,
        { primaryIdentifier, captchaToken },
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async changePrimaryIdentifier(data) {
    try {
      const response = await post(
        requestEndpoint.authV2.changePrimaryIdentifier,
        data,
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async socialRegister(data) {
    try {
      const response = await post(
        requestEndpoint.authV2.socialRegister,
        data,
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async socialValidatePhone(data) {
    try {
      const response = await post(
        requestEndpoint.authV2.socialValidatePhone,
        data,
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async completeRecovery(prospectId, code) {
    try {
      const response = await post(
        requestEndpoint.authV2.completeRecovery,
        { prospectId, code },
        {},
      );
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static handleError(error) {
    // Si l'erreur vient déjà de l'API avec une structure connue
    if (error.response?.data) {
      const apiError = error.response.data;

      // Extrait un texte lisible depuis un message qui peut être une string ou un objet {fr, en}
      const extractMessageText = (msg) => {
        if (!msg) return null;
        if (typeof msg === "string") return msg;
        if (typeof msg === "object") {
          const lang = localStorage.getItem("tc-language-active") || "fr";
          return msg[lang] || msg.fr || msg.en || null;
        }
        return null;
      };

      const rawMessage = extractMessageText(apiError.message);

      // Mapping des clés d'erreur en messages user-friendly
      const errorMessages = {
        USER_UNDERAGE: "Vous devez être majeur pour vous inscrire",
        OTP_EXPIRED: "Le code de vérification a expiré",
        OTP_INVALID: "Code de vérification invalide",
        OTP_COOLDOWN_ACTIVE:
          rawMessage || "Veuillez attendre avant de renvoyer un code",
        PROSPECT_BLOCKED: rawMessage || "Compte temporairement bloqué",
        ACCOUNT_BLOCKED: rawMessage || "Compte temporairement bloqué",
        DISABLED_ACCOUNT:
          rawMessage || "Compte désactivé ! Veuillez l'activer pour continuer",
        OTP_MAX_ATTEMPTS: rawMessage || "Trop de tentatives. Compte bloqué.",
        NO_SECONDARY_CONTACT:
          "Aucun contact secondaire enregistré. Contactez le support.",
        IDENTIFIER_ALREADY_EXISTS: "Cet identifiant est déjà utilisé",
        PHONE_ALREADY_EXISTS:
          "Ce numéro de téléphone est déjà associé à un autre compte",
        NEW_IDENTIFIER_SAME_AS_OLD:
          "Le nouvel identifiant doit être différent de l'ancien",
        SECONDARY_CONTACT_SAME_AS_PRIMARY:
          "Le contact secondaire doit être différent du contact principal",
        CAPTCHA_INVALID: "La vérification anti-robot a échoué",
      };

      const message =
        errorMessages[apiError.errorKey] ||
        rawMessage ||
        "Une erreur est survenue";

      // Créer une erreur enrichie qui préserve les données de l'API
      const enrichedError = new Error(message);
      enrichedError.response = error.response; // Préserver la réponse complète
      enrichedError.apiData = apiError; // Ajouter les données API directement

      return enrichedError;
    }

    // Erreurs réseau ou autres
    if (error.code === "NETWORK_ERROR") {
      return new Error(
        "Erreur de connexion. Veuillez vérifier votre internet.",
      );
    }

    if (error.code === "TIMEOUT") {
      return new Error(
        "Le serveur met trop de temps à répondre. Veuillez réessayer.",
      );
    }

    return new Error("Une erreur inattendue est survenue. Veuillez réessayer.");
  }

  static validateIdentifier(identifier) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;

    if (emailRegex.test(identifier)) {
      return { type: "EMAIL", isValid: true };
    }

    if (phoneRegex.test(identifier)) {
      return { type: "PHONE", isValid: true };
    }

    return { type: null, isValid: false };
  }

  static validateAge(birthdate) {
    const birthDate = new Date(birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return {
      isValid: age >= 18,
      age: age,
      message: age < 18 ? "Vous devez être majeur pour vous inscrire" : null,
    };
  }

  static formatTimeRemaining(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    }

    return `${remainingSeconds}s`;
  }

  static maskContact(contact) {
    if (contact.includes("@")) {
      // Email: user@example.com -> u***@example.com
      const [username, domain] = contact.split("@");
      return `${username.charAt(0)}***@${domain}`;
    } else {
      // Téléphone: +2250712345678 -> +225***5678
      return contact.slice(0, -4) + "***" + contact.slice(-4);
    }
  }
}

export default AuthV2Service;
